import React, { useEffect, useMemo, useState } from "react";
import { api, ROLES, SPECIALTIES, specialtyColor, roleLabel } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { toast } from "sonner";
import { Plus, X, Trash2, Pencil, Link2, AlertTriangle, Mail } from "lucide-react";

const inputCls = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none transition";

// Roles that draw payroll from clock-ins → must be linked to an HR employee
// so compensation stays sourced from the HR module.
const HR_LINK_ROLES = ["technician", "manager", "purchasing", "rh", "financeiro", "analista", "pm", "estimator"];

export default function Team() {
  const { t } = useLang();
  const [users, setUsers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]);
  const [subcontractors, setSubcontractors] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [areaFilter, setAreaFilter] = useState("");

  const load = () => Promise.all([
    api.get("/users").then(setUsers),
    api.get("/hr/employees?status=active").then(setEmployees).catch(() => setEmployees([])),
    api.get("/clients").then(setClients).catch(() => setClients([])),
    api.get("/subcontractors").then(setSubcontractors).catch(() => setSubcontractors([])),
  ]);
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ name: "", email: "", password: "", role: "technician", phone: "", hourly_rate: 0, standby_rate: "", lunch_deduction: 0, specialties: [], client_id: "", subcontractor_id: "", hr_employee_id: "" });
    setOpen(true);
  };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ ...u, specialties: u.specialties || [], password: "", hr_employee_id: u.hr_employee_id || "" });
    setOpen(true);
  };

  const toggleSpecialty = (s) => {
    const cur = form.specialties || [];
    setForm({ ...form, specialties: cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s] });
  };

  // When HR employee is picked, auto-fill comp fields from HR (source of truth).
  const applyHrEmployee = (eid) => {
    if (!eid) {
      setForm((f) => ({ ...f, hr_employee_id: "" }));
      return;
    }
    const emp = employees.find((e) => e.id === eid);
    if (!emp) { setForm((f) => ({ ...f, hr_employee_id: eid })); return; }
    setForm((f) => ({
      ...f,
      hr_employee_id: eid,
      name: f.name || emp.name || `${emp.first_name || ""} ${emp.last_name || ""}`.trim(),
      email: editing ? f.email : (f.email || emp.email || ""),
      phone: f.phone || emp.phone || "",
      hourly_rate: emp.hourly_rate ?? 0,
      standby_rate: emp.standby_rate ?? "",
      lunch_deduction: emp.lunch_deduction ?? 0,
    }));
  };

  const save = async () => {
    if (!form.name?.trim()) return toast.error(t("name"));
    try {
      const rateFields = {
        hourly_rate: parseFloat(form.hourly_rate) || 0,
        standby_rate: form.standby_rate === "" || form.standby_rate === undefined || form.standby_rate === null ? null : parseFloat(form.standby_rate),
        lunch_deduction: parseInt(form.lunch_deduction) || 0,
      };
      if (editing) {
        const payload = { name: form.name, role: form.role, phone: form.phone, ...rateFields, specialties: form.specialties || [], client_id: form.client_id, subcontractor_id: form.subcontractor_id || null, hr_employee_id: form.hr_employee_id || null };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success(t("saved"));
      } else {
        const created = await api.post("/users", { ...form, ...rateFields, specialties: form.specialties || [], subcontractor_id: form.subcontractor_id || null, hr_employee_id: form.hr_employee_id || null });
        // Surface welcome-email delivery status to the admin so silent bounces
        // don't leave the new user without credentials.
        const w = created?._welcome_email;
        if (!w?.attempted) {
          toast.success(`User created. ⚠️ Welcome email NOT sent (SendGrid not configured).`);
        } else if (w?.sent) {
          toast.success(`User created and welcome email sent to ${form.email}`);
        } else {
          toast.warning(`User created but welcome email failed: ${w?.error || "unknown"}. Use "Resend welcome" on the user row.`, { duration: 10000 });
        }
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error"));
    }
  };
  const resendWelcome = async (u) => {
    if (!window.confirm(`Resend welcome email to ${u.email}? A NEW temporary password will be generated.`)) return;
    try {
      await api.post(`/users/${u.id}/resend-welcome`);
      toast.success(`Welcome email re-sent to ${u.email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to resend");
    }
  };
  const remove = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    await api.del(`/users/${id}`);
    toast.success(t("deleted"));
    await load();
  };

  // Employees available to pick: unlinked ones + currently linked (when editing)
  const linkableEmployees = useMemo(() => {
    const cur = form.hr_employee_id;
    return employees.filter((e) => !e.user_id || e.id === cur);
  }, [employees, form.hr_employee_id]);
  const linkedEmp = employees.find((e) => e.id === form.hr_employee_id);
  const needsHrLink = HR_LINK_ROLES.includes(form.role);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">{t("technicians")}</h1>
        <button data-testid="new-user-btn" onClick={openNew} className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white text-sm font-medium px-4 py-2.5 hover:bg-slate-800 transition-colors">
          <Plus className="w-4 h-4" /> {t("newUser")}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button data-testid="area-filter-all" onClick={() => setAreaFilter("")} className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${!areaFilter ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{t("allAreas")}</button>
        {SPECIALTIES.map((s) => (
          <button key={s} data-testid={`area-filter-${s}`} onClick={() => setAreaFilter(areaFilter === s ? "" : s)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${areaFilter === s ? "bg-slate-900 text-white" : `${specialtyColor(s)} hover:opacity-80`}`}>
            {t(`sp_${s}`)}
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-xs font-bold uppercase tracking-wider text-slate-500">
              <th className="text-left px-4 py-3">{t("name")}</th>
              <th className="text-left px-4 py-3">{t("email")}</th>
              <th className="text-left px-4 py-3">{t("role")}</th>
              <th className="text-left px-4 py-3">HR</th>
              <th className="text-left px-4 py-3">{t("specialtyAreas")}</th>
              <th className="text-left px-4 py-3">{t("phone")}</th>
              <th className="text-right px-4 py-3">{t("hourlyRate")}</th>
              <th className="text-right px-4 py-3">{t("actions")}</th>
            </tr>
          </thead>
          <tbody>
            {users.filter((u) => !areaFilter || (u.specialties || []).includes(areaFilter)).map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                <td className="px-4 py-3 font-medium text-slate-900">{u.name}</td>
                <td className="px-4 py-3 text-slate-600">{u.email}</td>
                <td className="px-4 py-3"><span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 capitalize">{u.role}</span></td>
                <td className="px-4 py-3">
                  {u.hr_employee_id ? (
                    <span data-testid={`hr-link-${u.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                      <Link2 className="w-3 h-3" /> Linked
                    </span>
                  ) : HR_LINK_ROLES.includes(u.role) ? (
                    <span data-testid={`hr-unlinked-${u.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                      <AlertTriangle className="w-3 h-3" /> Not linked
                    </span>
                  ) : <span className="text-slate-300">—</span>}
                </td>
                <td className="px-4 py-3">
                  {u.role === "technician" && (u.specialties || []).length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {u.specialties.map((s) => (
                        <span key={s} data-testid={`badge-${u.id}-${s}`} className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${specialtyColor(s)}`}>{t(`sp_${s}`)}</span>
                      ))}
                    </div>
                  ) : <span className="text-slate-300">—</span>}
                </td>
                <td className="px-4 py-3 text-slate-600">{u.phone || "—"}</td>
                <td className="px-4 py-3 text-right text-slate-700 tabular-nums">
                  {u.role === "technician" ? (
                    <div>
                      <div>${(u.hourly_rate || 0).toFixed(2)}/h</div>
                      <div className="text-[10px] text-slate-400 font-normal">
                        {t("standbyRate")}: ${((u.standby_rate ?? u.hourly_rate) || 0).toFixed(2)} · {t("lunchDeduction")}: {u.lunch_deduction ? `${u.lunch_deduction}min` : "—"}
                      </div>
                    </div>
                  ) : "—"}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1.5">
                    <button data-testid={`resend-welcome-${u.id}`} title="Resend welcome email" onClick={() => resendWelcome(u)} className="p-1.5 rounded-md text-slate-500 hover:bg-blue-50 hover:text-blue-600"><Mail className="w-4 h-4" /></button>
                    <button data-testid={`edit-user-${u.id}`} onClick={() => openEdit(u)} className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900"><Pencil className="w-4 h-4" /></button>
                    <button data-testid={`delete-user-${u.id}`} onClick={() => remove(u.id)} className="p-1.5 rounded-md text-slate-500 hover:bg-red-50 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {users.length === 0 && <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-400">{t("noData")}</td></tr>}
          </tbody>
        </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-4 bg-black/40 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white z-10">
              <h2 className="text-lg font-semibold text-slate-900">{editing ? t("edit") : t("newUser")}</h2>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <div className="p-6 space-y-4">
              {/* HR Employee Link */}
              {needsHrLink && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3">
                  <label className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-emerald-800 mb-1.5">
                    <Link2 className="w-3.5 h-3.5" /> Link to HR employee
                    <span className="ml-auto text-[10px] font-medium text-emerald-700 normal-case tracking-normal">HR = source of truth for pay</span>
                  </label>
                  <select
                    data-testid="user-hr-employee"
                    value={form.hr_employee_id || ""}
                    onChange={(e) => applyHrEmployee(e.target.value)}
                    className={inputCls}
                  >
                    <option value="">— No link (unlinked user) —</option>
                    {linkableEmployees.map((e) => (
                      <option key={e.id} value={e.id}>
                        {e.employee_number} · {e.name} ({e.employment_type}) · {e.job_title || e.department}
                      </option>
                    ))}
                  </select>
                  {linkedEmp ? (
                    <p className="text-[11px] text-emerald-700 mt-1.5">
                      ✓ Linked to <b>{linkedEmp.name}</b> ({linkedEmp.employee_number}). Compensation is managed in HR Management.
                    </p>
                  ) : (
                    <p className="text-[11px] text-slate-500 mt-1.5">
                      Not linked. Rates entered below will be saved directly on the user record and can differ from HR.
                    </p>
                  )}
                </div>
              )}

              <div>
                <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("name")}</label>
                <input data-testid="user-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("email")}</label>
                <input data-testid="user-email" type="email" disabled={!!editing} value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} className={`${inputCls} disabled:bg-slate-100`} />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("password")} {editing && "(leave blank to keep)"}</label>
                <input data-testid="user-password" type="password" value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("role")}</label>
                  <select data-testid="user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls}>
                    {ROLES.map((r) => <option key={r} value={r}>{roleLabel(r, "en")} ({r})</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("phone")}</label>
                  <input data-testid="user-phone" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
                </div>
              </div>
              {form.role === "purchasing" && (
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("purchasingCeiling") || "Purchasing Ceiling ($)"}</label>
                  <input data-testid="user-ceiling" type="number" step="10" min="0" placeholder="0 = unlimited" value={form.purchasing_ceiling ?? ""} onChange={(e) => setForm({ ...form, purchasing_ceiling: e.target.value })} className={inputCls} />
                  <p className="text-[11px] text-slate-400 mt-1">{t("purchasingCeilingDesc") || "Requests above this amount are escalated to the Director of Operations."}</p>
                </div>
              )}
              {form.role === "technician" && (
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("specialtyAreas")}</label>
                  <div className="flex flex-wrap gap-2">
                    {SPECIALTIES.map((s) => {
                      const on = (form.specialties || []).includes(s);
                      return (
                        <button key={s} type="button" data-testid={`user-specialty-${s}`} onClick={() => toggleSpecialty(s)}
                          className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${on ? "bg-slate-900 text-white border-slate-900" : `${specialtyColor(s)} border-transparent hover:opacity-80`}`}>
                          {on ? "✓ " : ""}{t(`sp_${s}`)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {form.role === "technician" && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">
                      {t("hourlyRate")}{form.hr_employee_id && <span className="ml-1 text-[10px] font-normal text-emerald-600">🔗 from HR</span>}
                    </label>
                    <input data-testid="user-rate" type="number" step="0.5" min="0" value={form.hourly_rate ?? 0} disabled={!!form.hr_employee_id} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} className={`${inputCls} disabled:bg-emerald-50/50 disabled:text-emerald-800`} />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">
                      {t("standbyRate")}{form.hr_employee_id && <span className="ml-1 text-[10px] font-normal text-emerald-600">🔗 from HR</span>}
                    </label>
                    <input data-testid="user-standby-rate" type="number" step="0.5" min="0" placeholder={t("sameAsHourly")} value={form.standby_rate ?? ""} disabled={!!form.hr_employee_id} onChange={(e) => setForm({ ...form, standby_rate: e.target.value })} className={`${inputCls} disabled:bg-emerald-50/50 disabled:text-emerald-800`} />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">
                      {t("lunchDeduction")}{form.hr_employee_id && <span className="ml-1 text-[10px] font-normal text-emerald-600">🔗 from HR</span>}
                    </label>
                    <select data-testid="user-lunch" value={form.lunch_deduction ?? 0} disabled={!!form.hr_employee_id} onChange={(e) => setForm({ ...form, lunch_deduction: e.target.value })} className={`${inputCls} disabled:bg-emerald-50/50 disabled:text-emerald-800`}>
                      <option value={0}>{t("noDeduction")}</option>
                      <option value={30}>{t("min30")}</option>
                      <option value={60}>{t("min60")}</option>
                    </select>
                  </div>
                </div>
              )}
              {form.role === "client" && (
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">Client company *</label>
                  <select
                    data-testid="user-client-id"
                    value={form.client_id || ""}
                    onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                    className={inputCls}
                  >
                    <option value="">— Pick a client from the CRM —</option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}{c.city ? ` · ${c.city}` : ""}{c.contact_name ? ` (${c.contact_name})` : ""}
                      </option>
                    ))}
                  </select>
                  {clients.length === 0 ? (
                    <p className="text-[11px] text-amber-600 mt-1">
                      No clients registered yet. Go to Clients page and create one before adding a client login.
                    </p>
                  ) : (
                    <p className="text-[11px] text-slate-500 mt-1">
                      The user will only see this client&apos;s tickets, projects, and invoices in the Client Portal.
                    </p>
                  )}
                </div>
              )}
              {form.role === "subcontractor" && (
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">Subcontractor company *</label>
                  <select
                    data-testid="user-subcontractor-id"
                    value={form.subcontractor_id || ""}
                    onChange={(e) => setForm({ ...form, subcontractor_id: e.target.value })}
                    className={inputCls}
                  >
                    <option value="">— Pick a subcontractor from the registry —</option>
                    {subcontractors.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}{s.trades?.length ? ` · ${s.trades.join(", ")}` : ""}
                      </option>
                    ))}
                  </select>
                  {subcontractors.length === 0 ? (
                    <p className="text-[11px] text-amber-600 mt-1">
                      No subcontractors registered yet. Go to Subcontractors page and add one before creating a sub login.
                    </p>
                  ) : (
                    <p className="text-[11px] text-slate-500 mt-1">
                      The user will only see service calls where their company was assigned. Check-in / check-out available. No pricing / cost info is exposed.
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200 sticky bottom-0 bg-white">
              <button onClick={() => setOpen(false)} className="rounded-lg px-4 py-2 text-sm font-medium border border-slate-300 hover:bg-slate-50">{t("cancel")}</button>
              <button data-testid="user-save" onClick={save} className="rounded-lg px-4 py-2 text-sm font-medium bg-slate-900 text-white hover:bg-slate-800">{t("save")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
